import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  Avatar,
  Badge,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import { threadApi, getStatusDisplay, getTypeDisplay, THREAD_STATUS, THREAD_TYPE } from '../services/api';
import { useAuth } from '../context/AuthContext';

// Format timestamp
const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// Thread Card Component
const ThreadCard = ({ thread, onClick }) => {
  const statusInfo = getStatusDisplay(thread.status);
  const typeInfo = getTypeDisplay(thread.thread_type);
  const isUrgent = thread.status === THREAD_STATUS.AWAITING_NUTRITIONIST;

  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        border: isUrgent ? '2px solid' : '1px solid',
        borderColor: isUrgent ? 'warning.main' : 'divider',
        bgcolor: isUrgent ? 'warning.light' : 'background.paper',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box display="flex" alignItems="flex-start" gap={2}>
          {/* Avatar */}
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              thread.has_unread_from_user ? (
                <MarkEmailUnreadIcon sx={{ fontSize: 16, color: 'warning.main' }} />
              ) : null
            }
          >
            <Avatar
              sx={{
                bgcolor: thread.thread_type === THREAD_TYPE.COMPLEX_CASE_EVALUATION
                  ? 'error.light'
                  : 'success.light',
                color: thread.thread_type === THREAD_TYPE.COMPLEX_CASE_EVALUATION
                  ? 'error.main'
                  : 'success.main',
              }}
            >
              <PersonIcon />
            </Avatar>
          </Badge>

          {/* Content */}
          <Box flex={1} minWidth={0}>
            {/* Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {thread.user_first_name}
              </Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {formatTime(thread.updated_at)}
                </Typography>
              </Box>
            </Box>

            {/* Reference ID */}
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              {thread.reference_id}
            </Typography>

            {/* Chips */}
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip
                label={typeInfo.label}
                size="small"
                color={typeInfo.color}
                variant="outlined"
              />
              <Chip
                label={statusInfo.label}
                size="small"
                color={statusInfo.color}
                variant={isUrgent ? 'filled' : 'outlined'}
              />
              {thread.has_unread_from_user && (
                <Chip
                  label="New message"
                  size="small"
                  color="warning"
                  variant="filled"
                />
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

function Queue() {
  const navigate = useNavigate();
  const { user, sessionKey } = useAuth();
  const [threads, setThreads] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const status = statusFilter !== 'all' ? statusFilter : null;
      const type = typeFilter !== 'all' ? typeFilter : null;
      const data = await threadApi.getQueue(status, type);
      setThreads(data.threads || []);
      setCounts(data.counts || {});
    } catch (err) {
      setError('Failed to load queue. Please try again.');
      console.error('Queue fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter]);

  // Reset data and refetch when user changes or new login session starts
  useEffect(() => {
    setThreads([]);
    setCounts({});
    setLoading(true);
    fetchQueue();
  }, [user?.id, sessionKey, fetchQueue]);

  // Filter threads by search query
  const filteredThreads = threads.filter((thread) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      thread.user_first_name?.toLowerCase().includes(query) ||
      thread.reference_id?.toLowerCase().includes(query)
    );
  });

  // Sort threads: AWAITING_NUTRITIONIST first, then by updated_at
  const sortedThreads = [...filteredThreads].sort((a, b) => {
    if (a.status === THREAD_STATUS.AWAITING_NUTRITIONIST && b.status !== THREAD_STATUS.AWAITING_NUTRITIONIST) {
      return -1;
    }
    if (b.status === THREAD_STATUS.AWAITING_NUTRITIONIST && a.status !== THREAD_STATUS.AWAITING_NUTRITIONIST) {
      return 1;
    }
    return new Date(b.updated_at) - new Date(a.updated_at);
  });

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="secondary.dark">
            Queue
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {counts.awaiting_nutritionist || 0} threads need your response
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchQueue} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stats Cards */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <Card sx={{ flex: '1 1 150px', minWidth: 150 }}>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h3" fontWeight={700} color="warning.main">
              {counts.awaiting_nutritionist || 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Needs Response
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 150px', minWidth: 150 }}>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h3" fontWeight={700} color="info.main">
              {counts.awaiting_user || 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Awaiting User
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 150px', minWidth: 150 }}>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h3" fontWeight={700} color="secondary.main">
              {counts.total_open || 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total Open
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            {/* Search */}
            <TextField
              placeholder="Search by name or reference..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ minWidth: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Status filter */}
            <ToggleButtonGroup
              value={statusFilter}
              exclusive
              onChange={(_, val) => val && setStatusFilter(val)}
              size="small"
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value={THREAD_STATUS.AWAITING_NUTRITIONIST}>
                Needs Response
              </ToggleButton>
              <ToggleButton value={THREAD_STATUS.AWAITING_USER}>
                Awaiting User
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Type filter */}
            <ToggleButtonGroup
              value={typeFilter}
              exclusive
              onChange={(_, val) => val && setTypeFilter(val)}
              size="small"
            >
              <ToggleButton value="all">All Types</ToggleButton>
              <ToggleButton value={THREAD_TYPE.COMPLEX_CASE_EVALUATION}>
                Complex Case
              </ToggleButton>
              <ToggleButton value={THREAD_TYPE.WEEKLY_CHECKIN}>
                Check-in
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : sortedThreads.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary">
              No threads found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchQuery ? 'Try adjusting your search or filters' : 'Your queue is empty'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          {sortedThreads.map((thread) => (
            <ThreadCard
              key={thread.reference_id}
              thread={thread}
              onClick={() => navigate(`/thread/${thread.reference_id}`)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

export default Queue;
